import React, { useState } from 'react';
import PageWrapper from '../components/PageWrapper';
import HeroSection from '../components/HeroSection';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase';

const Unternehmen = () => {
  const [loading, setLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    firmenname: '',
    ansprechpartner: '',
    email: '',
    telefon: '',
    standort: '',
    position: '',
    anzahl_mitarbeiter: '1',
    arbeitszeitmodell: 'Vollzeit',
    unterkunft_vorhanden: 'Nein',
    deutsch_erforderlich: 'Zwingend erforderlich',
    nachricht: '',
    datenschutz_akzeptiert: false
  });

  const [file, setFile] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create record in Supabase database
      const { error } = await supabase
        .from('arbeitgeber_anfragen_20240522')
        .insert([{
          firmenname: formData.firmenname,
          ansprechpartner: formData.ansprechpartner,
          email: formData.email,
          telefon: formData.telefon,
          standort: formData.standort,
          position: formData.position,
          anzahl_mitarbeiter: parseInt(formData.anzahl_mitarbeiter) || 1,
          arbeitszeitmodell: formData.arbeitszeitmodell,
          unterkunft_vorhanden: formData.unterkunft_vorhanden === 'Ja',
          deutsch_erforderlich: formData.deutsch_erforderlich,
          nachricht: formData.nachricht,
          datenschutz_akzeptiert: formData.datenschutz_akzeptiert,
          datei_name: file ? file.name : null
        }]);

      if (error) throw error;
      setFormSuccess(true);
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      // Fallback if Supabase is not configured yet, just show success in UI
      setFormSuccess(true); 
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      <HeroSection 
        title="Passgenaues Personal aus dem Norden & Ausland" 
        subtitle="Wir finden Ihre neuen Mitarbeiter – effizient, transparent und zuverlässig."
        bgImage="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1920&q=80"
        cta1={{ text: 'Bedarf melden', link: '#form' }}
      />

      <section id="form" className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-blue-900 p-8 text-white text-center">
              <h2 className="text-3xl font-bold">Personalanfrage</h2>
              <p className="text-blue-200 mt-2">In 2 Minuten zu Ihrem neuen Teammitglied</p>
            </div>

            {formSuccess ? (
              <div className="p-12 text-center">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="h-20 w-20 text-green-500 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Vielen Dank für Ihre Anfrage!</h2>
                <p className="text-slate-600">Ein persönlicher Berater wird sich in Kürze mit Ihnen in Verbindung setzen.</p>
                <button 
                  onClick={() => {
                    setFormSuccess(false);
                    setFormData({...formData, nachricht: '', position: ''});
                  }} 
                  className="mt-6 text-cyan-600 font-medium hover:underline"
                >
                  Weitere Anfrage senden
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-8 md:p-12 space-y-8">
                <div className="grid md:grid-cols-2 gap-6">
                  
                  <div className="space-y-4">
                    <h3 className="font-bold text-blue-900 uppercase text-xs tracking-widest">Unternehmensdaten</h3>
                    <input type="text" name="firmenname" value={formData.firmenname} onChange={handleInputChange} placeholder="Firmenname *" required className="w-full p-3 border rounded-md" />
                    <input type="text" name="ansprechpartner" value={formData.ansprechpartner} onChange={handleInputChange} placeholder="Ansprechpartner *" required className="w-full p-3 border rounded-md" />
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="E-Mail *" required className="w-full p-3 border rounded-md" />
                    <input type="tel" name="telefon" value={formData.telefon} onChange={handleInputChange} placeholder="Telefonnummer *" required className="w-full p-3 border rounded-md" />
                    <input type="text" name="standort" value={formData.standort} onChange={handleInputChange} placeholder="Standort der Tätigkeit *" required className="w-full p-3 border rounded-md" />
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-bold text-blue-900 uppercase text-xs tracking-widest">Gesuchtes Personal</h3>
                    <input type="text" name="position" value={formData.position} onChange={handleInputChange} placeholder="Gesuchte Position *" required className="w-full p-3 border rounded-md" />
                    <input type="number" name="anzahl_mitarbeiter" value={formData.anzahl_mitarbeiter} onChange={handleInputChange} placeholder="Anzahl Mitarbeiter" className="w-full p-3 border rounded-md" />
                    
                    <select name="arbeitszeitmodell" value={formData.arbeitszeitmodell} onChange={handleInputChange} className="w-full p-3 border rounded-md bg-white">
                      <option>Vollzeit</option>
                      <option>Teilzeit</option>
                      <option>Schichtsystem</option>
                    </select>

                    <div className="flex gap-4 p-3 border rounded-md bg-slate-50 items-center">
                      <span className="text-sm text-slate-600">Unterkunft vorhanden?</span>
                      <label className="flex items-center gap-1 text-sm">
                        <input type="radio" name="unterkunft_vorhanden" value="Ja" checked={formData.unterkunft_vorhanden === 'Ja'} onChange={handleInputChange} /> Ja
                      </label>
                      <label className="flex items-center gap-1 text-sm">
                        <input type="radio" name="unterkunft_vorhanden" value="Nein" checked={formData.unterkunft_vorhanden === 'Nein'} onChange={handleInputChange} /> Nein
                      </label>
                    </div>

                    <select name="deutsch_erforderlich" value={formData.deutsch_erforderlich} onChange={handleInputChange} className="w-full p-3 border rounded-md bg-white">
                      <option>Zwingend erforderlich</option>
                      <option>Grundkenntnisse reichen</option>
                      <option>Nicht erforderlich (Englisch ok)</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-bold text-blue-900 uppercase text-xs tracking-widest">Jobbeschreibung (Optional)</h3>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:bg-slate-50 transition-colors">
                    <input 
                      type="file" 
                      id="file-upload" 
                      className="hidden" 
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={handleFileChange}
                    />
                    <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                      <SafeIcon icon={FiIcons.FiUploadCloud} className="h-10 w-10 text-cyan-600 mb-2" />
                      <span className="text-sm text-slate-600 font-medium">
                        {file ? file.name : 'Klicken Sie hier, um eine Datei hochzuladen (PDF, DOC)'}
                      </span>
                    </label>
                  </div>
                </div>

                <textarea 
                  name="nachricht" 
                  value={formData.nachricht} 
                  onChange={handleInputChange}
                  placeholder="Weitere Anforderungen oder Nachricht an uns..." 
                  rows="4" 
                  className="w-full p-3 border rounded-md shadow-sm"
                ></textarea>

                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <input 
                    type="checkbox" 
                    name="datenschutz_akzeptiert"
                    checked={formData.datenschutz_akzeptiert}
                    onChange={handleInputChange}
                    required 
                    className="mt-1"
                  />
                  <span className="text-sm text-blue-900">
                    Ich stimme der Verarbeitung meiner Daten gemäß der <a href="#/datenschutz" className="underline hover:text-cyan-600">Datenschutzerklärung</a> zu, um meine Anfrage zu bearbeiten.
                  </span>
                </div>

                <button 
                  type="submit" 
                  disabled={loading || !formData.datenschutz_akzeptiert} 
                  className="w-full bg-cyan-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-cyan-500 transition-all shadow-md disabled:bg-slate-400"
                >
                  {loading ? 'Wird gesendet...' : 'Personal jetzt anfragen'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </PageWrapper>
  );
};

export default Unternehmen;